// File: src/pages/Signin.jsx
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../utils/api';
import defaultLogo from '../assets/Empower-logo.png';
import { useNavigate } from 'react-router-dom';
import Loader from '../Components/Loader/Loader';
import { escapeHtml } from '../utils/sanitize';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';

const Signin = () => {
  const navigate = useNavigate();
  const { tenant, loading: tenantLoading } = useTenant();
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Get the logo to display - use signinLogo if available, then logoUrl, then default
  const rawLogo = tenant?.signinLogoUrl || tenant?.logoUrl || defaultLogo;
  const isDefaultLogo = !tenant?.signinLogoUrl && !tenant?.logoUrl;
  const logoToDisplay = rawLogo && !rawLogo.startsWith('data:') ? rawLogo + (rawLogo.includes('?') ? '&' : '?') + '_v=' + Date.now() : rawLogo;

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
      password: Yup.string()
        .min(6, 'Password must be at least 6 characters')
        .required('Password is required'),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      setMessage('');

      try {
        // Sanitize input before sending
        const sanitizedValues = {
          email: escapeHtml(values.email.trim().toLowerCase()),
          password: values.password, // Don't escape password as it might contain special chars
        };

        const response = await api.post('/api/super-admin-login', sanitizedValues);
        setMessage(response.data.message);
        setError('');

        login(response.data.token, response.data.admin);
        navigate('/dashboard');
      } catch (error) {
        setError(error.response?.data?.message || 'Something went wrong.');
        setMessage('');
      } finally {
        setLoading(false);
      }
    },
  });

  if (authLoading || tenantLoading) return <Loader />;

  return (
    <>
      {loading && <Loader />}
      <div className="signin-container">
        <div className="bg-image-container">
          <img
            src={logoToDisplay}
            alt={tenant?.brandName || 'Logo'}
            className={isDefaultLogo ? 'default-logo' : ''}
          />
        </div>
        <div className="form-container">
          <form className="form" onSubmit={formik.handleSubmit} autoComplete="off">
            <div className="signin-heading">
              <h1>Super Admin Login</h1>
              <p>Login to access your account</p>
            </div>

            <div>
              <div className="flex-column">
                <label>Email <span style={{color:'red'}}>*</span></label>
              </div>
              <div className="inputForm">
                <input
                  type="text"
                  name="email"
                  placeholder="Enter your Email"
                  className="input"
                  autoComplete="off"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={loading}
                />
              </div>
              {formik.touched.email && formik.errors.email && (
                <div style={{ color: 'red', fontSize: '13px' }}>{formik.errors.email}</div>
              )}
            </div>

            <div>
              <div className="flex-column">
                <label>Password <span style={{color:'red'}}>*</span></label>
              </div>
              <div className="inputForm" style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Enter your Password"
                  className="input"
                  autoComplete="off"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={loading}
                />
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: '#6c757d'
                  }}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
              {formik.touched.password && formik.errors.password && (
                <div style={{ color: 'red', fontSize: '13px' }}>{formik.errors.password}</div>
              )}
            </div>

            <div className="flex-row">
              <div className="remember-input">
                <input type="radio" id="remember" name="remember" disabled={loading} />
                <label htmlFor="remember">Remember me</label>
              </div>
              <span
                className="span"
                onClick={loading ? null : () => navigate('/forgot-password')}
                style={{
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                Forgot password?
              </span>
            </div>

            {message && <p className="success-message">{message}</p>}
            {error && <p className="error-message">{error}</p>}

            <button
              type="submit"
              className="button-submit"
              disabled={loading || !formik.isValid || !formik.values.email || !formik.values.password}
              style={{
                opacity:
                  loading || !formik.isValid || !formik.values.email || !formik.values.password
                    ? 0.6
                    : 1,
                cursor:
                  loading || !formik.isValid || !formik.values.email || !formik.values.password
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              Sign In
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default Signin;
